/**
 * Order display-derivation helpers (UID-98 PR-A, WS-5). PURE, framework-agnostic.
 *
 * These synthesize the human-facing order surfaces the design calls for
 * (`UID-5-cart-checkout-orders-design-gap-analysis.md` §B/§D) WITHOUT new columns:
 *   - {@link formatOrderRef} — the ONE canonical "SO-…" order number (promoted here
 *     from the checkout vertical so the orders list + detail + post-checkout animation
 *     all format the same way, closing gap §B "order identity is inconsistent").
 *   - {@link deriveOrderName} — the human order name, synthesized from the first line's
 *     part label (prototype behaviour: group / first-part name). No `order.name` column
 *     (gap analysis §B / "Per-story DB impact": order name = derived, no column).
 *   - {@link deriveOrderEta} — the order-level est-delivery, rolled up from the existing
 *     per-line `deliveryPromise.committedDates` (gap analysis §D: ETA rollup is
 *     frontend-only, no migration).
 *   - {@link derivePaymentDisplayLabel} — the orders-list "Payment type" cell (UID-132),
 *     derived from the ALREADY-frozen `Order.payment` snapshot (ADR-0038 §1). No new
 *     stored field: this only projects the existing checkout-time payment selection down
 *     to the short label the list/Home-widget rows show (e.g. "CC •••• 4242").
 *
 * Each takes a MINIMAL structural slice (mirroring `OrderTotalInput` in ./order) so the
 * same rule applies to the shared {@link import('./order').Order} AND the SPA's
 * contract-generated `Order`/`OrderSummary` — one rule, one home; a list value and a
 * detail value for the same order can never diverge.
 */

/**
 * A friendly, human-readable order reference for display (e.g. "SO-24815") derived
 * DETERMINISTICALLY from the order's UUID. The UUID stays the routing/API key; this is
 * presentation only. Demo-grade until the Order aggregate carries a real order number.
 *
 * Behaviour-preserving move from `apps/web/.../checkout-domain.ts` (UID-98 PR-A) — the
 * single canonical SO-number formatter now reused across list/detail (S5/S6) and the
 * post-checkout animation.
 */
export function formatOrderRef(orderId: string): string {
  // UID-130: fold the ENTIRE id, not just the first 8 hex chars. The demo seed's
  // order ids share a common prefix (`d3305eed-0000-4000-8000-00000d00000N`) and
  // differ only in their tail, so the old first-8-hex slice collapsed every seeded
  // order to the SAME "SO-52605". Folding all hex digits gives ids that differ
  // anywhere (prefix OR tail) distinct SO numbers, while staying deterministic and
  // presentation-only. Real (random-UUID) orders were already distinct and remain so.
  //
  // CALLOUT (code-review): because the SO number is a pure render-time derivation and
  // is NEVER persisted (there is no SO-number column anywhere in the schema), changing
  // this fold changes EVERY order's displayed SO number globally — not only the
  // demo-seeded ones. That is expected: the SO ref is a demo-grade, non-stable display
  // value (the UUID remains the authoritative id). Anyone holding a screenshot of an old
  // SO number (e.g. for a demo walkthrough) should expect it to differ after this change.
  const hex = orderId.replace(/[^0-9a-f]/gi, '')
  let n = 0
  for (const ch of hex) {
    n = (n * 31 + Number.parseInt(ch, 16)) % 90000
  }
  return `SO-${(10000 + n).toString()}`
}

/**
 * A friendly, human-readable quote reference for display (e.g. "Q-24815") derived
 * DETERMINISTICALLY from the quote's id — mirroring {@link formatOrderRef}'s "SO-…"
 * treatment. Quote ids are UUIDs in real data (randomUUID at CPQ write-back), so the
 * raw value is NOT human-readable; this renders a stable "Q-#####" instead so the
 * order-detail "Reference quote #" (UID-212) never shows a bare UUID. Presentation
 * only — the raw id stays the API/routing key. Demo-grade until a real quote number
 * exists on the contract.
 */
export function formatQuoteRef(quoteId: string): string {
  const hex = quoteId.replace(/[^0-9a-f]/gi, '')
  let n = 0
  for (const ch of hex) {
    n = (n * 31 + Number.parseInt(ch, 16)) % 90000
  }
  return `Q-${(10000 + n).toString()}`
}

/** The minimal structural slice {@link deriveOrderName} reads: each line's optional label. */
export interface OrderNameInput {
  readonly lines: readonly { readonly displayName?: string | null }[]
}

/**
 * Synthesize the human order name from the first line that carries a part label
 * (prototype behaviour: group / first-part name). Returns `null` when no line has a
 * usable label — the caller falls back to the SO number ({@link formatOrderRef}).
 * Whitespace-only labels are treated as absent.
 */
export function deriveOrderName(order: OrderNameInput): string | null {
  for (const line of order.lines) {
    const name = line.displayName
    if (typeof name === 'string' && name.trim().length > 0) {
      return name
    }
  }
  return null
}

/** The minimal structural slice {@link deriveOrderEta} reads: each line's promised dates. */
export interface OrderEtaInput {
  readonly lines: readonly {
    readonly deliveryPromise?: { readonly committedDates?: readonly string[] } | null
  }[]
}

/**
 * Roll an order-level est-delivery up from the per-line delivery promises: the LATEST
 * committed date across every line (the order is delivered when its last line is).
 * Returns `null` when no line carries a committed date. ISO dates (YYYY-MM-DD) compare
 * lexicographically = chronologically, so no Date parsing is needed.
 */
export function deriveOrderEta(order: OrderEtaInput): string | null {
  let latest: string | null = null
  for (const line of order.lines) {
    for (const date of line.deliveryPromise?.committedDates ?? []) {
      if (latest === null || date > latest) {
        latest = date
      }
    }
  }
  return latest
}

/** The minimal structural slice {@link derivePaymentDisplayLabel} reads — the frozen
 * `PaymentSelection` snapshot (or `null` for a pre-checkout/pre-UID-60 order). A
 * discriminated union mirroring `PaymentSelection` (./checkout.ts) so `poNumber` is
 * required (never optional/undefined) on the `po_invoice` branch, matching the real
 * type exactly — TypeScript's control-flow narrowing then guarantees `poNumber` is a
 * real string by the time {@link derivePaymentDisplayLabel} reaches that branch,
 * with no runtime fallback needed. */
export type PaymentDisplayInput =
  | { readonly kind: 'card'; readonly displayLabel?: string | null }
  | { readonly kind: 'ach'; readonly displayLabel?: string | null }
  | { readonly kind: 'po_invoice'; readonly poNumber: string }

/** Pulls just the masked-digits portion (e.g. "•••• 4242") out of a `displayLabel` that
 * may or may not carry a card-brand prefix ("Visa •••• 3433", "•••• 4242", …) — the
 * orders-list cell shows the generic method abbreviation ("CC"/"ACH") itself, so a
 * brand name would otherwise be duplicated ("CC Visa •••• 3433"). Matches any run of
 * two-or-more mask-ish characters (bullets, asterisks, low lines, dots, or plain
 * whitespace) immediately followed by digits and trailing the string — covering
 * "•••• 4242", "**** 4242", "xxxx4242", "... 4242", etc. — then trims any leading
 * separator whitespace picked up ahead of the mask run (e.g. the space between a
 * brand prefix and the mask). Returns an empty string when the label doesn't match
 * ANY recognized mask shape — callers must treat that as "no legible mask to show"
 * and render the bare abbreviation, never the raw label verbatim (which could
 * re-introduce brand-name duplication for an unrecognized separator style). */
function maskedDigits(label: string): string {
  const match = label.match(/[•*x_.\s]{2,}\d+$/i)
  return match ? match[0].trim() : ''
}

/**
 * Derive the orders-list "Payment type" cell (UID-132) from the order's frozen,
 * as-placed `payment` snapshot (ADR-0038 §1) — real data that already exists on every
 * placed order, projected down to a short label matching the design's `paymentLabel`
 * (`Fictiv Platform.dc.html` ~L9077: literally `'CC •••• 4242'`). `null` payment (an
 * order placed before UID-60, or never checked out) yields `null` — the caller renders
 * the same "—" every other unpopulated summary field uses; this is NOT the "Project"
 * gap (§ — genuinely no backing field), it is a real, always-derivable value once
 * `payment` is present.
 */
export function derivePaymentDisplayLabel(
  payment: PaymentDisplayInput | null | undefined,
): string | null {
  if (!payment) return null
  if (payment.kind === 'card') {
    const masked = payment.displayLabel ? maskedDigits(payment.displayLabel) : ''
    return masked ? `CC ${masked}` : 'CC'
  }
  if (payment.kind === 'ach') {
    const masked = payment.displayLabel ? maskedDigits(payment.displayLabel) : ''
    return masked ? `ACH ${masked}` : 'ACH'
  }
  // `payment` is narrowed to the `po_invoice` member here (TypeScript control-flow
  // analysis after the two `if`s above return), so `poNumber` is a real, required
  // string — no `?? '—'` fallback is reachable or needed.
  return `PO #${payment.poNumber}`
}
